import React, { useState, useEffect } from "react";
import { 
  Calendar, 
  Clock, 
  MapPin, 
  Users, 
  Sparkles, 
  ChevronDown, 
  Check, 
  Scissors, 
  Award, 
  BookOpen, 
  Heart, 
  Star, 
  X, 
  ArrowRight, 
  ChevronRight, 
  Instagram, 
  Camera, 
  ShieldCheck, 
  FileText,
  Volume2,
  Lock,
  Download,
  Palette,
  Play,
  HeartHandshake
} from "lucide-react";

// Realistic signup notifications to stimulate conversions
const STUDENT_SIGNUPS = [
  { name: "Meera Krishnan", location: "Kochi", time: "1 min ago" },
  { name: "Aishwarya Rai", location: "Mumbai", time: "3 mins ago" },
  { name: "Anjali Gupta", location: "Delhi", time: "7 mins ago" },
  { name: "Srinidhi Reddy", location: "Hyderabad", time: "11 mins ago" },
  { name: "Tanya Sen", location: "Kolkata", time: "15 mins ago" }
];

export default function OneMonthFabricWorkshop() {
  // --- States ---
  const [seatsRemaining, setSeatsRemaining] = useState(14);
  const [timeLeft, setTimeLeft] = useState({ hours: 1, minutes: 54, seconds: 22 });
  const [activeTab, setActiveTab] = useState(1);
  const [recentSignup, setRecentSignup] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  
  // Lead form states
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    level: "beginner"
  });
  
  // Registration success / ticket pass state
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [ticketDetails, setTicketDetails] = useState(null);
  
  // FAQ search and open states
  const [faqSearch, setFaqSearch] = useState("");
  const [openFaq, setOpenFaq] = useState({});

  // --- Effects ---
  
  // Countdown Timer
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        } else {
          return { hours: 2, minutes: 15, seconds: 0 };
        }
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Decreasing Seats FOMO simulation
  useEffect(() => {
    const seatInterval = setInterval(() => {
      setSeatsRemaining(prev => {
        if (prev > 5) {
          const drop = Math.random() > 0.6 ? 1 : 0;
          return prev - drop;
        }
        return prev; // keep min 5 seats
      });
    }, 28000);
    return () => clearInterval(seatInterval);
  }, []);

  // Recent Signup Notifications (Toasts)
  useEffect(() => {
    let signupIndex = 0;
    const triggerNotification = () => {
      setRecentSignup(STUDENT_SIGNUPS[signupIndex]);
      signupIndex = (signupIndex + 1) % STUDENT_SIGNUPS.length;
      
      setTimeout(() => {
        setRecentSignup(null);
      }, 5500);
    };

    const firstTimeout = setTimeout(triggerNotification, 6000);
    const interval = setInterval(triggerNotification, 32000);

    return () => {
      clearTimeout(firstTimeout);
      clearInterval(interval);
    };
  }, []);

  // --- Handlers ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) {
      alert("Please fill out all fields.");
      return;
    }

    const uniqueTicketId = `FP-${Math.floor(200000 + Math.random() * 700000)}`;
    const ticket = {
      ...formData,
      ticketId: uniqueTicketId,
      date: "Monday, March 2, 2026",
      time: "4:00 PM - 5:30 PM IST (Mon-Fri)",
      seatNo: `P-${Math.floor(1 + Math.random() * 40)}`
    };

    // Save lead to local storage (Static site client-side storage)
    try {
      const existingLeads = JSON.parse(localStorage.getItem("fairy_creations_fabric_leads") || "[]");
      existingLeads.push({ ...ticket, timestamp: new Date().toISOString() });
      localStorage.setItem("fairy_creations_fabric_leads", JSON.stringify(existingLeads));
    } catch (err) {
      console.warn("Could not write to localStorage:", err);
    }

    setTicketDetails(ticket);
    setRegistrationSuccess(true);
    setSeatsRemaining(prev => Math.max(prev - 1, 4));
  };

  const closeSuccessPass = () => {
    setRegistrationSuccess(false);
    setIsModalOpen(false);
    setTicketDetails(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      level: "beginner"
    });
  };

  const toggleFaq = (idx) => {
    setOpenFaq(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  // --- Mock/Aspirational Data ---
  const CURRICULUM_SECTIONS = [
    {
      id: 1,
      title: "Week 1: Fundamentals, Prep & Brush Mastery",
      topics: [
        "Introduction to fabric painting & canvas selection",
        "Understanding different types of fabrics (natural vs blends)",
        "Fabric preparation & starch removal before painting",
        "Selecting the correct colors & mediums for different fabrics",
        "Brush control, holding pressure, and continuous stroke application",
        "Color mixing formulas for vibrant non-fading pigments"
      ]
    },
    {
      id: 2,
      title: "Week 2: Advanced Blending & 4-Kurti/Saree Projects",
      topics: [
        "Painting with fabric colors & high-grade acrylic colors",
        "Premium blending, shading, and smooth organic gradient effects",
        "Creating beautiful complex floral and geometric fabric patterns",
        "Designing balanced compositions on garments before painting",
        "Working on large scale fabric panels (Kurtis and Sarees)"
      ]
    },
    {
      id: 3,
      title: "Week 3: Silk Painting & Acramine Dye Mastery",
      topics: [
        "The specialized process of painting on premium Silk fabrics",
        "Controlling the flow & spread of color on fine silk fibers",
        "Acramine dye formulation and textile application methods",
        "Achieving vibrant resistance color effects & custom combinations",
        "Working on T-Shirts and Tote Bags with mixed media"
      ]
    },
    {
      id: 4,
      title: "Week 4: Metallic Gilding, Block Printing & Finishing",
      topics: [
        "Creating gorgeous metallic effects and brilliant highlights",
        "Adding decorative metallic detailing using physical relief colors",
        "Introduction to traditional Hand Block Printing alignment",
        "Critical Pre-Care and Post-Care washing & iron-fix methods",
        "Finishing techniques: How to make your work look clean and professionally finished"
      ]
    }
  ];

  const CREATION_PROJECTS = [
    {
      title: "4 Kurtis & Saree Masterpieces",
      count: "4 Projects",
      image: "https://images.unsplash.com/photo-1591625591034-75d303d2e1a4?crop=entropy&cs=srgb&fm=jpg&q=85",
      desc: "Learn to layout gorgeous botanical borders and central motifs on sarees and kurtis."
    },
    {
      title: "3 Designer Canvas Tote Bags",
      count: "3 Projects",
      image: "https://images.unsplash.com/photo-1569728808392-4c637feee42b?crop=entropy&cs=srgb&fm=jpg&q=85",
      desc: "Design heavy-textured, everyday usable cotton totes featuring custom patterns."
    },
    {
      title: "2 Customized Streetwear T-Shirts",
      count: "2 Projects",
      image: "https://images.unsplash.com/photo-1561822713-da02f57225b2?crop=entropy&cs=srgb&fm=jpg&q=85",
      desc: "Apply clean acrylic-mixed graphic lettering and modern illustrative strokes on organic cotton."
    },
    {
      title: "1 Hand-Painted Designer Top",
      count: "1 Project",
      image: "https://images.unsplash.com/photo-1585706818211-6e0aa515339f?crop=entropy&cs=srgb&fm=jpg&q=85",
      desc: "Create delicate pastel motifs on casual linen tops."
    },
    {
      title: "1 Elegant Silk Scarf",
      count: "1 Project",
      image: "https://images.unsplash.com/photo-1444362408440-274ecb6fc730?crop=entropy&cs=srgb&fm=jpg&q=85",
      desc: "Practice silk flow control on lightweight, luxury silk scarves with metallic highlights."
    },
    {
      title: "1 Statement Denim/Canvas Jacket",
      count: "1 Project",
      image: "https://images.unsplash.com/photo-1568288796918-03e7d93306bd?crop=entropy&cs=srgb&fm=jpg&q=85",
      desc: "Paint a bold, high-contrast, head-turning back panel on heavy-weight structured outerwear."
    }
  ];

  const FAQS = [
    {
      q: "Is this workshop suitable for complete beginners?",
      a: "Yes. It is designed to teach everything step by step. You don't need any prior drawing, sketching, or painting experience to join and create beautiful garments."
    },
    {
      q: "Where will the classes happen?",
      a: "Classes will be conducted live through Zoom, 5 days a week. You can ask questions in real-time and get direct feedback from the instructor."
    },
    {
      q: "How long is the workshop?",
      a: "One complete month of live interactive learning and supervised project crafting."
    },
    {
      q: "How many days a week are the classes?",
      a: "Five days a week (Monday to Friday) to ensure consistent hands-on practice."
    },
    {
      q: "Will I receive recordings?",
      a: "Yes. HD session recordings will be uploaded daily and provided with 5 complete years of unlimited access, so you can rewatch at your own convenience."
    },
    {
      q: "Will I receive a certificate?",
      a: "Yes, a physical co-signed Certificate of Fabric Painting Artistry from Fairy Creations will be provided upon successful completion of your projects."
    },
    {
      q: "Do I need to know drawing or painting already?",
      a: "No. Beginners are highly welcome. We provide carbon templates and outline techniques that make drawing completely optional."
    },
    {
      q: "What projects will we create?",
      a: "Students will work on 4 Kurtis/Sarees, 3 tote bags, 2 T-shirts, 1 top, 1 scarf, 1 jacket, and other practical fabric-painting swatch exercises."
    }
  ];

  const searchedFaqs = FAQS.filter(faq => 
    faq.q.toLowerCase().includes(faqSearch.toLowerCase()) || 
    faq.a.toLowerCase().includes(faqSearch.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#FDFCF9] text-[#24211F] font-sans antialiased selection:bg-[#EC4899]/20 selection:text-[#EC4899]">
      
      {/* 1. TOP URGENCY BANNER */}
      <section 
        data-testid="top-urgency-banner" 
        className="bg-gradient-to-r from-[#1E3A8A] via-[#EC4899] to-[#D48B38] text-white text-xs sm:text-sm py-2.5 px-4 font-medium text-center sticky top-0 z-50 shadow-md"
      >
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-6">
          <div className="flex items-center gap-1.5 font-mono">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
            <span>LIVE TIMER:</span>
            <span className="bg-black/30 px-2 py-0.5 rounded font-bold tracking-widest">
              {String(timeLeft.hours).padStart(2, '0')}:{String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span>
              Only <strong className="font-mono text-white underline decoration-pink-300 decoration-2">{seatsRemaining}</strong> seats left for the month
            </span>
            <button 
              onClick={() => setIsModalOpen(true)}
              data-testid="lead-capture-modal-trigger"
              className="bg-white text-[#EC4899] hover:bg-stone-50 text-[10px] sm:text-xs font-bold uppercase tracking-wider py-1 px-3.5 rounded-full transition-all duration-300 hover:scale-105 active:scale-95"
            >
              REGISTER NOW
            </button>
          </div>
        </div>
      </section>

      {/* 2. NAVBAR */}
      <header className="bg-[#FDFCF9]/95 backdrop-blur-md border-b border-[#EBE4D8] sticky top-[44px] sm:top-[40px] z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex justify-between items-center">
          
          {/* Small & Elegant Brand Logo */}
          <div data-testid="nav-brand-logo" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#1E3A8A]/10 flex items-center justify-center border border-[#1E3A8A]/10">
              <Palette className="w-4.5 h-4.5 text-[#1E3A8A]" />
            </div>
            <div>
              <span className="block font-serif font-bold text-base sm:text-lg tracking-tight text-[#24211F]">
                Fairy Creations
              </span>
              <span className="block text-[8px] text-[#5C5248] tracking-widest uppercase font-mono">
                learn.fairycreations.in
              </span>
            </div>
          </div>

          {/* Navigation Anchors */}
          <nav className="hidden md:flex items-center gap-8 text-xs sm:text-sm font-semibold tracking-wide text-[#5C5248]">
            <a href="#curriculum" data-testid="nav-agenda-link" className="hover:text-[#EC4899] transition-colors uppercase">Curriculum</a>
            <a href="#projects" className="hover:text-[#EC4899] transition-colors uppercase">What You'll Create</a>
            <a href="#format" className="hover:text-[#EC4899] transition-colors uppercase">Format</a>
            <a href="#instructor" data-testid="nav-instructor-link" className="hover:text-[#EC4899] transition-colors uppercase">Instructor</a>
            <a href="#gallery" data-testid="nav-gallery-link" className="hover:text-[#EC4899] transition-colors uppercase">Gallery</a>
            <a href="#faq" data-testid="nav-faq-link" className="hover:text-[#EC4899] transition-colors uppercase">FAQ</a>
          </nav>

          {/* Action button */}
          <button 
            onClick={() => setIsModalOpen(true)}
            data-testid="lead-capture-modal-trigger"
            className="bg-[#1E3A8A] hover:bg-[#12255C] text-white text-xs sm:text-sm font-bold uppercase tracking-wider py-2 sm:py-2.5 px-4 sm:px-6 rounded-lg transition-all"
          >
            REGISTER
          </button>

        </div>
      </header>

      {/* 3. HERO SECTION */}
      <section className="relative overflow-hidden pt-12 pb-20 sm:py-20 lg:py-24 border-b border-[#EBE4D8] bg-[#FAF8F5]">
        
        {/* Subtle artistic paint stroke decoration background */}
        <div className="absolute top-1/10 right-0 w-80 h-80 bg-[#EC4899]/5 rounded-full filter blur-3xl -z-10" />
        <div className="absolute bottom-1/10 left-1/10 w-96 h-96 bg-[#D48B38]/5 rounded-full filter blur-3xl -z-10" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Column: Heading, pitch & CTAs */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#EC4899]/10 border border-[#EC4899]/20 text-xs tracking-wider uppercase font-mono font-bold text-[#EC4899] mx-auto lg:mx-0">
                <Sparkles className="w-3.5 h-3.5" />
                <span>1-Month Certified Live Course</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-black tracking-tight text-[#24211F] leading-[1.12]">
                One-Month Complete <br className="hidden sm:inline" />
                <span className="text-[#1E3A8A] relative inline-block">
                  Fabric Painting
                  <span className="absolute bottom-1 left-0 w-full h-1.5 bg-[#EC4899]/30 -z-10" />
                </span> Workshop
              </h1>

              <h2 className="text-lg sm:text-xl lg:text-2xl font-serif font-semibold text-[#5C5248] italic max-w-2xl mx-auto lg:mx-0">
                Turn Plain Fabrics Into Beautiful Hand-Painted Creations — Even If You’re a Beginner
              </h2>

              <p className="text-[#24211F]/90 text-sm sm:text-base lg:text-lg leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Join a complete 1-month LIVE online fabric painting experience on Zoom, conducted 5 days a week, where you’ll learn practical fabric-painting techniques step by step and apply them to real wearable and usable projects.
              </p>

              {/* Trust proof row */}
              <div className="py-2 border-t border-b border-[#EBE4D8]/60 flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
                <div className="flex -space-x-2.5">
                  <div className="w-8 h-8 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center text-[10px] font-bold font-mono border-2 border-white">FC</div>
                  <div className="w-8 h-8 rounded-full bg-[#EC4899] text-white flex items-center justify-center text-[10px] font-bold font-mono border-2 border-white">FP</div>
                  <div className="w-8 h-8 rounded-full bg-[#D48B38] text-white flex items-center justify-center text-[10px] font-bold font-mono border-2 border-white">14K</div>
                </div>
                <p className="text-xs sm:text-sm text-[#5C5248] font-medium text-center sm:text-left">
                  Join <strong className="text-[#24211F] font-bold font-serif">14,000+ students</strong> who have already learned creative art techniques with us.
                </p>
              </div>

              {/* CTAs */}
              <div className="pt-2 space-y-3">
                <button
                  onClick={() => setIsModalOpen(true)}
                  data-testid="hero-claim-seat-button"
                  className="w-full sm:w-auto bg-[#EC4899] hover:bg-[#D946EF] text-white font-bold uppercase tracking-wider py-4 px-8 rounded-xl transition-all duration-300 shadow-md flex items-center justify-center gap-2 group hover:scale-102 active:scale-98"
                >
                  <span>REGISTER FOR THE WORKSHOP</span>
                  <ArrowRight className="w-4.5 h-4.5 group-hover:translate-x-1 transition-transform" />
                </button>
                <p className="text-xs text-[#5C5248] font-mono tracking-wide">
                  Limited Seats • Live on Zoom • Beginner Friendly
                </p>
              </div>

            </div>

            {/* Right Column: Sophisticated, realistic hero artwork image */}
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-2xl overflow-hidden shadow-xl border border-[#EBE4D8] aspect-[4/5] bg-stone-100">
                <img 
                  src="https://images.unsplash.com/photo-1591625591034-75d303d2e1a4?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Beautifully hand-painted premium fabric cloth artwork"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#24211F]/80 via-[#24211F]/20 to-transparent" />
                
                {/* Visual seat fomo progress bar card overlay */}
                <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur p-4 rounded-xl border border-[#EBE4D8] space-y-3">
                  <div className="flex justify-between items-center text-xs">
                    <span className="font-bold text-[#1E3A8A] font-serif uppercase tracking-wider">Live Seat Counter</span>
                    <span 
                      data-testid="live-seat-counter-badge"
                      className="bg-rose-50 text-rose-600 px-2 py-0.5 rounded font-mono font-bold text-[10px]"
                    >
                      {seatsRemaining} Seats Open
                    </span>
                  </div>
                  <div className="w-full h-2 bg-stone-100 rounded-full overflow-hidden">
                    <div 
                      data-testid="seat-progress-bar"
                      className="h-full bg-gradient-to-r from-[#EC4899] to-[#D48B38] transition-all duration-1000"
                      style={{ width: `${((20 - seatsRemaining + 60) / 80) * 100}%` }}
                    />
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 4. PREMIUM VIDEO SECTION */}
      <section className="py-16 sm:py-20 bg-white border-b border-[#EBE4D8] text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          
          <h2 className="text-2xl sm:text-3xl font-serif tracking-tight text-[#24211F] mb-3">
            See What You’ll Create Inside the Workshop
          </h2>
          
          {/* Large 16:9 Video Placeholder Container */}
          <div className="relative aspect-[16/9] w-full rounded-2xl overflow-hidden bg-[#24211F] shadow-lg border border-[#EBE4D8]/80 group mb-6">
            {!videoPlaying ? (
              <div className="absolute inset-0 flex flex-col justify-center items-center p-6 bg-gradient-to-br from-black/80 via-black/60 to-black/80">
                <button
                  onClick={() => setVideoPlaying(true)}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/95 text-[#1E3A8A] hover:text-[#EC4899] flex items-center justify-center transition-all duration-300 shadow-md group-hover:scale-110 active:scale-95 border border-white"
                  aria-label="Play promotional video"
                >
                  <Play className="w-8 h-8 sm:w-10 sm:h-10 fill-current translate-x-0.5" />
                </button>
                <div className="mt-4 text-white space-y-1 text-center">
                  <span className="block text-xs uppercase tracking-widest font-mono text-amber-300 font-bold">Promotional Workshop Clip</span>
                  <span className="block text-[10px] sm:text-xs text-stone-300">Placeholder for your 16:9 custom video upload</span>
                </div>
              </div>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-[#1E3A8A]/10 text-white">
                <p className="text-sm font-serif italic text-stone-300">
                  *Video playback started. Replace this tag with your video frame in the next phase!*
                </p>
                <button 
                  onClick={() => setVideoPlaying(false)}
                  className="absolute top-4 right-4 text-white bg-black/40 hover:bg-black/60 w-8 h-8 rounded-full flex items-center justify-center border border-white/20"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
            {/* Visual painting backdrop background for mockup */}
            <img 
              src="https://images.unsplash.com/photo-1561822713-da02f57225b2?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Artistic painting tools placeholder background"
              className="w-full h-full object-cover opacity-25 pointer-events-none"
            />
          </div>

          <p className="text-xs sm:text-sm text-[#5C5248] max-w-2xl mx-auto mb-6">
            Watch the video to discover what you’ll learn, create and experience during this one-month workshop.
          </p>

          <button
            onClick={() => setIsModalOpen(true)}
            data-testid="lead-capture-modal-trigger"
            className="bg-[#1E3A8A] hover:bg-[#12255C] text-white font-bold text-xs sm:text-sm uppercase tracking-wider py-3.5 px-8 rounded-lg transition-all hover:-translate-y-0.5"
          >
            YES, I WANT TO JOIN
          </button>

        </div>
      </section>

      {/* 5. MASTER 4 CORE TECHNIQUES */}
      <section className="py-20 sm:py-24 bg-[#FDFCF9] border-b border-[#EBE4D8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center space-y-4 mb-16">
            <span className="text-xs uppercase tracking-[0.2em] font-mono font-bold text-[#EC4899] block">
              Core Training Syllabus
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif tracking-tight text-[#24211F]">
              Master 4 Complete Fabric Art Techniques
            </h2>
            <p className="text-sm sm:text-base text-[#5C5248] max-w-2xl mx-auto">
              Our step-by-step program is built systematically around these 4 core modules. No shortcuts, no stencil paint tricks — only true creative fabric artistry.
            </p>
          </div>

          {/* Core 4 Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            
            {/* Card 1: Fabric Painting */}
            <div className="bg-white border border-[#EBE4D8] rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden flex flex-col justify-between min-h-[250px]">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#1E3A8A]" />
                  <h3 className="text-xl sm:text-2xl font-serif font-bold text-[#24211F]">
                    1. Fabric Painting
                  </h3>
                </div>
                <p className="text-sm sm:text-base text-[#5C5248] leading-relaxed">
                  Learn complete fabric painting from the basics, including fabric preparation, design placement, brush handling, colour mixing, blending, shading and creating clean professional finishes. Students will learn how to work with both fabric colours and acrylic colours on different types of fabrics.
                </p>
              </div>
            </div>

            {/* Card 2: Silk Painting */}
            <div className="bg-white border border-[#EBE4D8] rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden flex flex-col justify-between min-h-[250px]">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#EC4899]" />
                  <h3 className="text-xl sm:text-2xl font-serif font-bold text-[#24211F]">
                    2. Silk Painting
                  </h3>
                </div>
                <p className="text-sm sm:text-base text-[#5C5248] leading-relaxed">
                  Learn the specialised process of painting on silk, including colour application, controlling the flow of colour, creating clean designs and achieving elegant finishes on silk fabric.
                </p>
              </div>
            </div>

            {/* Card 3: Acramine Dye Painting */}
            <div className="bg-white border border-[#EBE4D8] rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden flex flex-col justify-between min-h-[250px]">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#2E7D32]" />
                  <h3 className="text-xl sm:text-2xl font-serif font-bold text-[#24211F]">
                    3. Acramine Dye Painting
                  </h3>
                </div>
                <p className="text-sm sm:text-base text-[#5C5248] leading-relaxed">
                  Learn how to work with Acramine dyes for textile painting, including correct application, colour effects, combinations and techniques for creating vibrant fabric designs.
                </p>
              </div>
            </div>

            {/* Card 4: Metallic Painting */}
            <div className="bg-white border border-[#EBE4D8] rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden flex flex-col justify-between min-h-[250px]">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#D48B38]" />
                  <h3 className="text-xl sm:text-2xl font-serif font-bold text-[#24211F]">
                    4. Metallic Painting
                  </h3>
                </div>
                <p className="text-sm sm:text-base text-[#5C5248] leading-relaxed">
                  Learn how to create beautiful metallic effects, highlights and decorative detailing using metallic colours on fabric.
                </p>
              </div>
            </div>

          </div>

          {/* Supporting Skill Highlight banner */}
          <div className="mt-12 bg-[#FAF8F5] border border-[#EBE4D8] rounded-xl p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="space-y-2 text-center sm:text-left">
              <span className="text-[10px] font-mono uppercase tracking-widest font-bold text-[#D48B38] block">Included Supporting Skills</span>
              <p className="text-sm sm:text-base text-[#24211F] font-serif font-semibold">
                You’ll also be introduced to traditional block printing, advanced patterns, working on different fabrics, pre-care, post-care and finishing techniques.
              </p>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              data-testid="lead-capture-modal-trigger"
              className="bg-[#1E3A8A] hover:bg-[#12255C] text-white text-xs font-bold uppercase tracking-wider px-5 py-3 rounded-lg transition-all whitespace-nowrap shrink-0"
            >
              Learn More
            </button>
          </div>

        </div>
      </section>

      {/* 6. DETAILED CURRICULUM ACCORDION */}
      <section id="curriculum" className="py-20 sm:py-24 bg-[#FAF8F5] border-b border-[#EBE4D8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          
          <div className="text-center space-y-3 mb-14">
            <span className="text-xs uppercase tracking-[0.15em] font-mono font-bold text-[#EC4899] block">
              Curriculum Roadmap
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif tracking-tight text-[#24211F]">
              Everything You’ll Learn
            </h2>
            <p className="text-sm text-[#5C5248]">
              We break down the entire month of structured live classes into easily scannable weekly cards.
            </p>
          </div>

          {/* Structured Curriculum Cards */}
          <div className="space-y-6">
            {CURRICULUM_SECTIONS.map((section) => (
              <div 
                key={section.id}
                className="bg-white border border-[#EBE4D8] rounded-xl p-6 sm:p-8 shadow-sm space-y-4"
              >
                <div className="flex justify-between items-center border-b border-[#EBE4D8]/50 pb-3">
                  <h3 className="font-serif font-bold text-base sm:text-lg text-[#1E3A8A]">
                    {section.title}
                  </h3>
                  <span className="text-[10px] font-mono uppercase bg-amber-50 text-amber-800 px-2.5 py-0.5 rounded font-semibold border border-amber-200">
                    Week 0{section.id}
                  </span>
                </div>

                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm text-[#5C5248]">
                  {section.topics.map((topic, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#EC4899] mt-2 shrink-0" />
                      <span>{topic}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 7. WHAT YOU WILL CREATE (Visually exciting showcase) */}
      <section id="projects" className="py-20 sm:py-24 bg-[#FDFCF9] border-b border-[#EBE4D8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center space-y-4 mb-16">
            <span className="text-xs uppercase tracking-[0.2em] font-mono font-bold text-[#EC4899] block">
              Hands-On Projects
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif tracking-tight text-[#24211F]">
              You Won’t Just Learn — You’ll Actually Create
            </h2>
            <p className="text-sm sm:text-base text-[#5C5248] max-w-2xl mx-auto">
              By the end of the workshop, you won’t just have learned techniques — you’ll have a collection of beautiful, hand-painted pieces created by you.
            </p>
          </div>

          {/* Project Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {CREATION_PROJECTS.map((proj, i) => (
              <article 
                key={i}
                data-testid="gallery-item-card"
                className="bg-white border border-[#EBE4D8] rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col"
              >
                {/* Visual Image representation */}
                <div className="relative aspect-[4/3] bg-stone-100 overflow-hidden">
                  <img 
                    src={proj.image} 
                    alt={proj.title} 
                    className="w-full h-full object-cover hover:scale-103 transition-transform duration-500 cursor-pointer"
                    onClick={() => setLightboxImage(proj)}
                  />
                  <span className="absolute top-4 left-4 bg-white/95 text-[#EC4899] font-mono font-bold text-[10px] px-3 py-1 rounded-full border border-[#EBE4D8]">
                    {proj.count}
                  </span>
                </div>

                <div className="p-5 flex-1 flex flex-col justify-between space-y-2">
                  <h3 className="font-serif font-bold text-base sm:text-lg text-[#24211F]">
                    {proj.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#5C5248] leading-relaxed">
                    {proj.desc}
                  </p>
                </div>
              </article>
            ))}
          </div>

          {/* Register CTA spacing */}
          <div className="pt-12 text-center">
            <button
              onClick={() => setIsModalOpen(true)}
              data-testid="lead-capture-modal-trigger"
              className="bg-[#EC4899] hover:bg-[#D946EF] text-white font-bold uppercase tracking-wider py-4 px-10 rounded-xl transition-all shadow-md inline-flex items-center gap-2 hover:scale-102"
            >
              <span>YES, I WANT TO CREATE THESE PIECES</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>

        </div>
      </section>

      {/* 8. WORKSHOP FORMAT */}
      <section id="format" className="py-16 sm:py-20 bg-[#FAF8F5] border-b border-[#EBE4D8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <h2 className="text-2xl sm:text-3xl font-serif text-center text-[#24211F] mb-12">
            How the Workshop Works
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            
            <div className="bg-white border border-[#EBE4D8] p-5 rounded-xl text-center shadow-xs">
              <span className="block text-[10px] font-mono uppercase text-stone-400 font-semibold mb-1">Duration</span>
              <strong className="block font-serif text-base sm:text-lg text-[#1E3A8A]">1 Month</strong>
            </div>

            <div className="bg-white border border-[#EBE4D8] p-5 rounded-xl text-center shadow-xs">
              <span className="block text-[10px] font-mono uppercase text-stone-400 font-semibold mb-1">Classes</span>
              <strong className="block font-serif text-base sm:text-lg text-[#1E3A8A]">5 Days/Wk</strong>
            </div>

            <div className="bg-white border border-[#EBE4D8] p-5 rounded-xl text-center shadow-xs">
              <span className="block text-[10px] font-mono uppercase text-stone-400 font-semibold mb-1">Format</span>
              <strong className="block font-serif text-base sm:text-lg text-[#1E3A8A]">LIVE Online</strong>
            </div>

            <div className="bg-white border border-[#EBE4D8] p-5 rounded-xl text-center shadow-xs">
              <span className="block text-[10px] font-mono uppercase text-stone-400 font-semibold mb-1">Platform</span>
              <strong className="block font-serif text-base sm:text-lg text-[#1E3A8A]">Zoom App</strong>
            </div>

            <div className="bg-white border border-[#EBE4D8] p-5 rounded-xl text-center shadow-xs text-center">
              <span className="block text-[10px] font-mono uppercase text-stone-400 font-semibold mb-1">Skill level</span>
              <strong className="block font-serif text-base sm:text-lg text-[#1E3A8A]">Beginner</strong>
            </div>

            <div className="bg-white border border-[#EBE4D8] p-5 rounded-xl text-center shadow-xs">
              <span className="block text-[10px] font-mono uppercase text-stone-400 font-semibold mb-1">Recordings</span>
              <strong className="block font-serif text-xs sm:text-sm text-[#1E3A8A]">5 Yrs Access</strong>
            </div>

            <div className="bg-white border border-[#EBE4D8] p-5 rounded-xl text-center shadow-xs col-span-2 sm:col-span-1">
              <span className="block text-[10px] font-mono uppercase text-stone-400 font-semibold mb-1">Certification</span>
              <strong className="block font-serif text-xs sm:text-sm text-[#1E3A8A]">Included</strong>
            </div>

          </div>

        </div>
      </section>

      {/* 9. WHO THIS WORKSHOP IS FOR */}
      <section className="py-20 sm:py-24 bg-[#FDFCF9] border-b border-[#EBE4D8]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          
          <h2 className="text-3xl sm:text-4xl font-serif text-center tracking-tight text-[#24211F] mb-12">
            This Workshop Is Perfect For You If…
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-[#24211F]">
            
            <div className="flex items-start gap-3 bg-[#FAF8F5] p-5 rounded-xl border border-[#EBE4D8]/85">
              <Check className="w-5 h-5 text-[#2E7D32] stroke-[3] shrink-0 mt-0.5" />
              <p className="text-xs sm:text-sm leading-relaxed">
                <strong>You are completely new to fabric painting:</strong> Learn from absolute scratch without prior drawing or sketching experience.
              </p>
            </div>

            <div className="flex items-start gap-3 bg-[#FAF8F5] p-5 rounded-xl border border-[#EBE4D8]/85">
              <Check className="w-5 h-5 text-[#2E7D32] stroke-[3] shrink-0 mt-0.5" />
              <p className="text-xs sm:text-sm leading-relaxed">
                <strong>You love art, painting, textiles, or fashion:</strong> Expand your creative horizon and paint your design aesthetic directly onto clothes.
              </p>
            </div>

            <div className="flex items-start gap-3 bg-[#FAF8F5] p-5 rounded-xl border border-[#EBE4D8]/85">
              <Check className="w-5 h-5 text-[#2E7D32] stroke-[3] shrink-0 mt-0.5" />
              <p className="text-xs sm:text-sm leading-relaxed">
                <strong>You want to customize your own clothes:</strong> Transform old or plain kurtis, sarees, tops, and denims into head-turning designer apparel.
              </p>
            </div>

            <div className="flex items-start gap-3 bg-[#FAF8F5] p-5 rounded-xl border border-[#EBE4D8]/85">
              <Check className="w-5 h-5 text-[#2E7D32] stroke-[3] shrink-0 mt-0.5" />
              <p className="text-xs sm:text-sm leading-relaxed">
                <strong>You want to create personalized gifts:</strong> Paint premium organic t-shirts or cotton tote bags to gift your close family.
              </p>
            </div>

            <div className="flex items-start gap-3 bg-[#FAF8F5] p-5 rounded-xl border border-[#EBE4D8]/85">
              <Check className="w-5 h-5 text-[#2E7D32] stroke-[3] shrink-0 mt-0.5" />
              <p className="text-xs sm:text-sm leading-relaxed">
                <strong>You are a homemaker looking for a creative skill:</strong> Unwind and cultivate a rewarding, peaceful hobby right from the comfort of home.
              </p>
            </div>

            <div className="flex items-start gap-3 bg-[#FAF8F5] p-5 rounded-xl border border-[#EBE4D8]/85">
              <Check className="w-5 h-5 text-[#2E7D32] stroke-[3] shrink-0 mt-0.5" />
              <p className="text-xs sm:text-sm leading-relaxed">
                <strong>You run or want to start a handmade business:</strong> Learn commercial-grade post-care methods to launch a high-margin fabric painting shop on Instagram.
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* 10. BEGINNER OBJECTION SECTION */}
      <section className="bg-gradient-to-r from-[#1E3A8A]/5 via-[#EC4899]/5 to-[#1E3A8A]/5 py-16 px-4 text-center border-b border-[#EBE4D8]">
        <div className="max-w-2xl mx-auto space-y-4">
          <HeartHandshake className="w-10 h-10 text-[#EC4899] mx-auto mb-2" />
          <h2 className="text-2xl sm:text-3xl font-serif text-[#24211F]">
            “But I’ve Never Done Fabric Painting Before…”
          </h2>
          <p className="text-sm sm:text-base text-[#24211F]/90 leading-relaxed font-medium">
            That’s completely fine.
          </p>
          <p className="text-xs sm:text-sm text-[#5C5248] leading-relaxed">
            This workshop has been designed so that beginners can learn everything step by step — from understanding fabrics and colours to completing finished wearable projects. You don’t need to be a professional artist or have advanced drawing skills to join.
          </p>
        </div>
      </section>

      {/* 11. INSTRUCTOR / TRUST SECTION */}
      <section id="instructor" className="py-20 sm:py-24 bg-[#FAF8F5] border-b border-[#EBE4D8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left: Placeholder photo space */}
            <div className="lg:col-span-5 relative" data-testid="instructor-profile-card">
              <div className="relative rounded-2xl overflow-hidden aspect-[4/5] bg-stone-200 shadow-md border border-[#EBE4D8]">
                <img 
                  src="https://images.unsplash.com/photo-1788033955883-e7e938162658?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Aaradhya Sharma, Lead Fabric Artist"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 text-white">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-amber-300">Lead Design Educator</span>
                  <h3 className="text-xl font-serif font-bold">Fairy Creations Director</h3>
                </div>
              </div>
            </div>

            {/* Right: Narrative bio space */}
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs uppercase tracking-[0.15em] font-mono font-bold text-[#EC4899] block">
                Your Educator
              </span>
              <h2 className="text-3xl sm:text-4xl font-serif tracking-tight text-[#24211F]">
                Learn From an Experienced Creative Educator
              </h2>

              <div className="inline-flex items-center gap-2 bg-[#1E3A8A]/10 text-[#1E3A8A] px-4 py-2 rounded-xl text-sm font-bold font-mono">
                <Award className="w-4 h-4 text-[#D48B38]" />
                <span>14,000+ Students Taught</span>
              </div>

              {/* Editable space */}
              <div className="space-y-4 text-xs sm:text-sm text-[#5C5248] leading-relaxed">
                <p>
                  *<strong>Instructor Name Placeholder</strong>* (You can replace this with your name later). An experienced design graduate and active textile consultant, the director of Fairy Creations has trained over 14,000 students to discover the magic of paint on natural linens, heavy cottons, and delicate silks.
                </p>
                <p>
                  Our workshop bio space is ready for your personal bio: Share your achievements, passion, story, NIFT experience, and custom styling tips with your students here to build absolute credibility.
                </p>
                <p>
                  Join a growing community of 14,000+ students who have already learned creative techniques through our workshops.
                </p>
              </div>

              {/* Instagram link space */}
              <div className="pt-4 flex items-center gap-3">
                <a href="#" className="flex items-center gap-1.5 text-xs font-bold text-[#1E3A8A] hover:underline uppercase tracking-wider">
                  <Instagram className="w-4.5 h-4.5 text-[#EC4899]" />
                  <span>Follow fairy creations on Instagram</span>
                </a>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* 12. STUDENT RESULTS / SOCIAL PROOF PLACEHOLDERS */}
      <section className="py-20 sm:py-24 bg-white border-b border-[#EBE4D8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center space-y-3 mb-16">
            <span className="text-xs uppercase tracking-[0.15em] font-mono font-bold text-[#EC4899] block">
              Social Proof
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif tracking-tight text-[#24211F]">
              Loved By Our Students
            </h2>
            <p className="text-sm text-[#5C5248] max-w-xl mx-auto">
              We pride ourselves on 100% genuine creative growth. No fake names, fake ratings, or stock reviews. These placeholder spots are ready for your real student submissions.
            </p>
          </div>

          {/* Testimonials slots */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            
            <div data-testid="testimonial-card" className="bg-[#FDFCF9] border border-[#EBE4D8] p-6 sm:p-8 rounded-xl space-y-4">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />)}
              </div>
              <p className="text-xs sm:text-sm text-[#5C5248] leading-relaxed italic">
                "Placeholder for a real written review from your student. E.g. 'I painted my first silk saree with Aaradhya's technique and wore it to a wedding. Everyone was shocked that it was hand-painted!'"
              </p>
              <span className="block text-xs font-bold text-[#24211F]">— Student Review Slot 1</span>
            </div>

            <div data-testid="testimonial-card" className="bg-[#FDFCF9] border border-[#EBE4D8] p-6 sm:p-8 rounded-xl space-y-4">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />)}
              </div>
              <p className="text-xs sm:text-sm text-[#5C5248] leading-relaxed italic">
                "Placeholder for before/after creations. Show a flat plain kurti fabric next to a fully-shaded metallic golden highlight artwork panel."
              </p>
              <span className="block text-xs font-bold text-[#24211F]">— Student Review Slot 2</span>
            </div>

            <div data-testid="testimonial-card" className="bg-[#FDFCF9] border border-[#EBE4D8] p-6 sm:p-8 rounded-xl space-y-4">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />)}
              </div>
              <p className="text-xs sm:text-sm text-[#5C5248] leading-relaxed italic">
                "Placeholder for feedback screenshots or short student video links showing active textile designs during the one-month class."
              </p>
              <span className="block text-xs font-bold text-[#24211F]">— Student Review Slot 3</span>
            </div>

          </div>

        </div>
      </section>

      {/* 13. GALLERY SECTION (See what's possible) */}
      <section id="gallery" className="py-20 sm:py-24 bg-[#FDFCF9] border-b border-[#EBE4D8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center space-y-4 mb-14">
            <span className="text-xs uppercase tracking-[0.2em] font-mono font-bold text-[#EC4899] block">
              Inspiration Moodboard
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif tracking-tight text-[#24211F]">
              See What’s Possible
            </h2>
            <p className="text-sm sm:text-base text-[#5C5248] max-w-xl mx-auto">
              A premium, Pinterest-style editorial fashion-art mood board showcasing beautiful handmade textile swatches.
            </p>
          </div>

          {/* Pinterest-like grid of placeholder image slots */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-stone-100 rounded-xl overflow-hidden aspect-[3/4] border border-[#EBE4D8] relative group">
              <img src="https://images.unsplash.com/photo-1591625591034-75d303d2e1a4?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Kurti" className="w-full h-full object-cover group-hover:scale-103 transition-transform" />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-end p-4 transition-opacity">
                <span className="text-white text-xs font-serif">Kurtis Swatch</span>
              </div>
            </div>
            <div className="bg-stone-100 rounded-xl overflow-hidden aspect-[3/4] border border-[#EBE4D8] relative group">
              <img src="https://images.unsplash.com/photo-1444362408440-274ecb6fc730?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Saree" className="w-full h-full object-cover group-hover:scale-103 transition-transform" />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-end p-4 transition-opacity">
                <span className="text-white text-xs font-serif">Saree Swatch</span>
              </div>
            </div>
            <div className="bg-stone-100 rounded-xl overflow-hidden aspect-[3/4] border border-[#EBE4D8] relative group">
              <img src="https://images.unsplash.com/photo-1569728808392-4c637feee42b?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Tote Bag" className="w-full h-full object-cover group-hover:scale-103 transition-transform" />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-end p-4 transition-opacity">
                <span className="text-white text-xs font-serif">Tote Bags Swatch</span>
              </div>
            </div>
            <div className="bg-stone-100 rounded-xl overflow-hidden aspect-[3/4] border border-[#EBE4D8] relative group">
              <img src="https://images.unsplash.com/photo-1585706818211-6e0aa515339f?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Jacket" className="w-full h-full object-cover group-hover:scale-103 transition-transform" />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-end p-4 transition-opacity">
                <span className="text-white text-xs font-serif">Jackets Swatch</span>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* 14. WHAT YOU GET (Value stack list) */}
      <section className="py-20 sm:py-24 bg-[#FAF8F5] border-b border-[#EBE4D8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          
          <div className="bg-white border border-[#EBE4D8] rounded-2xl p-8 sm:p-12 shadow-sm space-y-8">
            
            <div className="text-center space-y-2">
              <span className="text-xs uppercase tracking-[0.2em] font-mono font-bold text-[#EC4899] block">Immediate Access</span>
              <h2 className="text-3xl font-serif text-[#24211F]">
                Everything Included With Your Registration
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs sm:text-sm text-[#24211F]">
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-[#2E7D32] stroke-[3]" />
                <span>✓ Complete 1-month workshop</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-[#2E7D32] stroke-[3]" />
                <span>✓ Live Zoom classes</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-[#2E7D32] stroke-[3]" />
                <span>✓ 5 days of learning every week</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-[#2E7D32] stroke-[3]" />
                <span>✓ Four core fabric-art techniques</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-[#2E7D32] stroke-[3]" />
                <span>✓ Step-by-step demonstrations</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-[#2E7D32] stroke-[3]" />
                <span>✓ Multiple real fabric projects</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-[#2E7D32] stroke-[3]" />
                <span>✓ Guidance on fabrics &amp; colors</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-[#2E7D32] stroke-[3]" />
                <span>✓ Pre-care and post-care methods</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-[#2E7D32] stroke-[3]" />
                <span>✓ Finishing techniques</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-[#2E7D32] stroke-[3]" />
                <span>✓ Session recordings access</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-[#2E7D32] stroke-[3]" />
                <span>✓ 5-year recording access</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-[#2E7D32] stroke-[3]" />
                <span>✓ Certificate after completion</span>
              </div>
            </div>

            <div className="text-center pt-4">
              <button
                onClick={() => setIsModalOpen(true)}
                data-testid="lead-capture-modal-trigger"
                className="bg-[#EC4899] hover:bg-[#D946EF] text-white font-bold uppercase tracking-wider py-4 px-10 rounded-xl transition-all shadow-md inline-flex items-center gap-2"
              >
                <span>RESERVE MY SEAT</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>

          </div>

        </div>
      </section>

      {/* 15. FAQ SECTION */}
      <section id="faq" className="py-20 sm:py-24 bg-[#FDFCF9] border-b border-[#EBE4D8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          
          <div className="text-center space-y-3 mb-12">
            <span className="text-xs uppercase tracking-[0.15em] font-mono font-bold text-[#EC4899] block">Common Queries</span>
            <h2 className="text-3xl sm:text-4xl font-serif text-[#24211F]">
              Frequently Asked Questions
            </h2>
          </div>

          {/* Search box */}
          <div className="max-w-md mx-auto mb-10">
            <input 
              type="text" 
              placeholder="Search curriculum FAQs..."
              value={faqSearch}
              onChange={(e) => setFaqSearch(e.target.value)}
              className="w-full bg-white border border-[#EBE4D8] rounded-xl px-4 py-3 text-sm text-[#24211F] placeholder:text-stone-300 focus:outline-none focus:ring-2 focus:ring-[#EC4899] transition-all"
            />
          </div>

          {/* Expandable items */}
          <div className="space-y-4">
            {searchedFaqs.length > 0 ? (
              searchedFaqs.map((faq, idx) => {
                const isOpen = !!openFaq[idx];
                return (
                  <div 
                    key={idx} 
                    data-testid="faq-accordion-item"
                    className="bg-white border border-[#EBE4D8] rounded-xl overflow-hidden transition-all shadow-xs"
                  >
                    <button
                      onClick={() => toggleFaq(idx)}
                      className="w-full text-left py-4.5 px-6 font-serif font-bold text-base text-[#24211F] flex justify-between items-center hover:bg-stone-50/50"
                    >
                      <span>{faq.q}</span>
                      <ChevronDown className={`w-5 h-5 text-[#5C5248] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <div className={`transition-all duration-300 overflow-hidden ${isOpen ? 'max-h-[300px] border-t border-[#EBE4D8]/50' : 'max-h-0'}`}>
                      <p className="p-6 text-xs sm:text-sm text-[#5C5248] leading-relaxed bg-[#FAF8F5]/40">
                        {faq.a}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-center text-sm text-stone-400 py-6">No matching FAQs found.</p>
            )}
          </div>

        </div>
      </section>

      {/* 16. FINAL CTA */}
      <section className="bg-gradient-to-b from-[#FAF8F5] to-[#FDFCF9] py-20 px-4 text-center border-b border-[#EBE4D8]">
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#EC4899]/10 text-[#EC4899] text-xs font-mono font-bold">
            <Sparkles className="w-3.5 h-3.5 animate-spin-slow" />
            <span>March Enrollment Open</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif tracking-tight text-[#24211F]">
            Ready to Turn Plain Fabric <br />Into Your Own Work of Art?
          </h2>
          
          <p className="text-xs sm:text-sm text-[#5C5248] max-w-lg mx-auto">
            Spend one month learning, experimenting and creating alongside us through live guided classes — and walk away with practical skills and beautiful pieces created with your own hands.
          </p>

          <div className="pt-4">
            <button
              onClick={() => setIsModalOpen(true)}
              data-testid="lead-capture-modal-trigger"
              className="bg-[#EC4899] hover:bg-[#D946EF] text-white font-bold uppercase tracking-wider py-4 px-10 rounded-xl transition-all shadow-md inline-flex items-center gap-2 hover:scale-102"
            >
              <span>JOIN THE ONE-MONTH FABRIC PAINTING WORKSHOP</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>

          <p className="text-xs text-stone-400 font-mono">
            Live on Zoom • 5 Days a Week • Beginner Friendly
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#24211F] text-stone-300 py-12 px-4 border-t border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6 text-center md:text-left">
          
          <div>
            <span className="block font-serif font-bold text-lg text-white">Fairy Creations</span>
            <span className="block text-xs text-stone-400 mt-1 font-mono">
              Online Fabric Painting Workshop | learn.fairycreations.in
            </span>
          </div>

          <p className="text-xs text-stone-500 font-mono">
            &copy; 2026 Fairy Creations. All rights reserved. <br />
            Designed inside TagMango Studio.
          </p>

        </div>
      </footer>

      {/* STICKY MOBILE CTA BAR */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-[#FDFCF9]/95 backdrop-blur-md border-t border-[#EBE4D8] py-3 px-4 z-40 flex items-center justify-between gap-4">
        <div>
          <span className="block text-[10px] text-stone-400 uppercase tracking-wider font-mono">Only 5 Seats Open</span>
          <span className="block text-xs text-[#EC4899] font-bold font-mono">March Batch</span>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          data-testid="sticky-mobile-claim-button"
          className="flex-1 bg-[#EC4899] hover:bg-[#D946EF] text-white text-xs font-bold uppercase tracking-wider py-3 px-5 rounded-lg transition-all text-center"
        >
          JOIN WORKSHOP
        </button>
      </div>

      {/* LEAD CAPTURE FORM / MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          
          <div className="bg-[#FDFCF9] border border-[#EBE4D8] max-w-lg w-full rounded-2xl overflow-hidden shadow-2xl relative">
            
            <button 
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 text-stone-500 hover:text-stone-700 w-8 h-8 rounded-full bg-white/50 flex items-center justify-center border border-stone-200"
              aria-label="Close form"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Success pass ticket */}
            {registrationSuccess && ticketDetails ? (
              <div className="p-6 sm:p-8 space-y-6 text-center" data-testid="digital-pass-card">
                <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-2 border border-emerald-200">
                  <Check className="w-6 h-6 stroke-[3]" />
                </div>

                <div className="space-y-1">
                  <span className="text-[10px] font-mono uppercase tracking-widest font-bold text-[#EC4899]">
                    Pass Generated ✦ Seat Confirmed
                  </span>
                  <h3 className="font-serif font-bold text-2xl text-[#24211F]">
                    You are in!
                  </h3>
                  <p className="text-xs text-[#5C5248]">
                    Your registration is confirmed. Show this ticket pass at Zoom start time!
                  </p>
                </div>

                {/* Ticket pass graphic details */}
                <div className="bg-white border-2 border-dashed border-[#EBE4D8] rounded-xl p-5 text-left space-y-4">
                  <div className="flex justify-between items-center border-b border-stone-100 pb-2">
                    <div>
                      <strong className="block font-serif text-sm text-[#24211F]">Fairy Creations</strong>
                      <span className="block text-[8px] font-mono text-stone-400">learn.fairycreations.in</span>
                    </div>
                    <span className="text-[10px] font-mono text-[#D48B38] font-bold">
                      ID: {ticketDetails.ticketId}
                    </span>
                  </div>

                  <div className="space-y-2 text-xs">
                    <div>
                      <span className="block text-[9px] uppercase tracking-wider text-stone-400 font-mono">Student</span>
                      <strong className="text-[#24211F] text-sm">{ticketDetails.name}</strong>
                    </div>
                    <div className="grid grid-cols-2 gap-3 pt-1 border-t border-stone-50">
                      <div>
                        <span className="block text-[9px] uppercase tracking-wider text-stone-400 font-mono">Date</span>
                        <strong className="text-stone-700">{ticketDetails.date}</strong>
                      </div>
                      <div>
                        <span className="block text-[9px] uppercase tracking-wider text-stone-400 font-mono">Live Time</span>
                        <strong className="text-stone-700">{ticketDetails.time}</strong>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 pt-1 border-t border-stone-50">
                      <div>
                        <span className="block text-[9px] uppercase tracking-wider text-stone-400 font-mono">WhatsApp alerts</span>
                        <strong className="text-stone-700 font-mono text-[11px]">{ticketDetails.phone}</strong>
                      </div>
                      <div>
                        <span className="block text-[9px] uppercase tracking-wider text-stone-400 font-mono">Email copy</span>
                        <strong className="text-stone-700 font-mono text-[11px] truncate block">{ticketDetails.email}</strong>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={closeSuccessPass}
                    data-testid="digital-pass-close-button"
                    className="flex-1 bg-[#1E3A8A] hover:bg-[#12255C] text-white py-3 rounded-lg text-xs font-bold uppercase tracking-wider transition-all"
                  >
                    Done
                  </button>
                </div>

              </div>
            ) : (
              // Lead Capture form view
              <form onSubmit={handleFormSubmit} className="p-6 sm:p-8 space-y-6">
                
                <div className="space-y-1.5 text-center sm:text-left">
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] font-bold text-[#EC4899]">
                    March Registration Open
                  </span>
                  <h3 className="font-serif font-bold text-xl text-[#24211F] leading-tight">
                    One-Month Fabric Painting Course
                  </h3>
                  <p className="text-xs text-[#5C5248]">
                    Submit your particulars to secure your Zoom link &amp; weekly study PDF roadmap.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 font-mono">
                      Full Name
                    </label>
                    <input 
                      type="text" 
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., Deepika Padukon"
                      data-testid="lead-form-name-input"
                      className="w-full bg-white border border-[#EBE4D8] rounded-xl px-4 py-3 text-sm text-[#24211F] placeholder:text-stone-300 focus:outline-none focus:ring-2 focus:ring-[#EC4899]"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 font-mono">
                      Email Address
                    </label>
                    <input 
                      type="email" 
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="e.g., deepika@gmail.com"
                      data-testid="lead-form-email-input"
                      className="w-full bg-white border border-[#EBE4D8] rounded-xl px-4 py-3 text-sm text-[#24211F] placeholder:text-stone-300 focus:outline-none focus:ring-2 focus:ring-[#EC4899]"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 font-mono">
                      WhatsApp Phone
                    </label>
                    <input 
                      type="tel" 
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="e.g., +91 98765 43210"
                      data-testid="lead-form-phone-input"
                      className="w-full bg-white border border-[#EBE4D8] rounded-xl px-4 py-3 text-sm text-[#24211F] placeholder:text-stone-300 focus:outline-none focus:ring-2 focus:ring-[#EC4899]"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 font-mono">
                      Experience level
                    </label>
                    <select
                      name="level"
                      value={formData.level}
                      onChange={handleInputChange}
                      data-testid="lead-form-level-select"
                      className="w-full bg-white border border-[#EBE4D8] rounded-xl px-4 py-3 text-sm text-[#24211F] focus:outline-none focus:ring-2 focus:ring-[#EC4899]"
                    >
                      <option value="beginner">Beginner (Curious hobbyist, first fold)</option>
                      <option value="intermediate">Intermediate (Painted fabric before)</option>
                      <option value="advanced">Advanced Designer</option>
                    </select>
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    data-testid="lead-form-submit-button"
                    className="w-full bg-[#EC4899] hover:bg-[#D946EF] text-white font-bold uppercase tracking-wider py-4 rounded-xl transition-all shadow-md flex items-center justify-center gap-2"
                  >
                    <span>Generate My Digital Pass Ticket</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

              </form>
            )}

          </div>
        </div>
      )}

      {/* LIGHTBOX PREVIEW MODAL */}
      {lightboxImage && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="relative max-w-3xl w-full bg-black rounded-2xl overflow-hidden shadow-2xl border border-stone-800">
            
            <button 
              onClick={() => setLightboxImage(null)}
              className="absolute top-4 right-4 text-white hover:text-stone-300 w-8 h-8 rounded-full bg-black/40 flex items-center justify-center border border-white/20 z-10"
            >
              <X className="w-5 h-5" />
            </button>

            <img 
              src={lightboxImage.image} 
              alt={lightboxImage.title} 
              className="w-full max-h-[70vh] object-contain"
            />

            <div className="bg-[#24211F] text-white p-6 space-y-2">
              <span className="text-[10px] font-mono text-[#D48B38] uppercase font-bold tracking-widest block">
                Artisan Creation
              </span>
              <h3 className="font-serif font-black text-lg">
                {lightboxImage.title}
              </h3>
              <p className="text-xs text-stone-400">
                Created using structural fabric painting techniques.
              </p>
            </div>

          </div>
        </div>
      )}

      {/* CLIENT RECENT SIGNUP TOAST ACCENT */}
      {recentSignup && (
        <div className="fixed bottom-20 md:bottom-6 left-4 bg-white border border-[#EBE4D8] rounded-xl p-4 shadow-xl z-50 flex items-center gap-3 max-w-sm animate-in fade-in slide-in-from-bottom-5 duration-300">
          <div className="w-8 h-8 rounded-full bg-[#EC4899]/10 text-[#EC4899] flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4" />
          </div>
          <div>
            <p className="text-xs text-[#24211F]">
              <strong>{recentSignup.name}</strong> from <span className="font-semibold text-[#1E3A8A]">{recentSignup.location}</span>
            </p>
            <span className="block text-[10px] text-stone-400 font-mono">
              Just reserved a seat {recentSignup.time}!
            </span>
          </div>
        </div>
      )}

    </div>
  );
}
